import { useState } from 'react'
import { InfoTooltip } from '@/components/InfoTooltip'
import { NumericField } from '@/components/NumericField'
import type { GlobalDefaultsFile, ProductTypeLimits } from '@/types/bundle'

type LimitField = 'max_documents' | 'max_total_pages' | 'max_pdf_size_mb'

export interface LimitsSectionProps {
  limits: ProductTypeLimits
  hardCaps: GlobalDefaultsFile['hard_caps']
  onChange: (limits: ProductTypeLimits) => void
  serverErrors?: Partial<Record<LimitField, string>>
}

const FIELD_CONFIG: { field: LimitField; label: string; step?: string }[] = [
  { field: 'max_documents', label: 'Max documents' },
  { field: 'max_total_pages', label: 'Max total pages' },
  { field: 'max_pdf_size_mb', label: 'Max PDF size (MB)', step: '0.1' },
]

// The 3 numeric inputs the design brief actually lists, plus
// document_ingestion_mode shown read-only below - a fixed enum
// ('inline_only' is currently the only supported value platform-wide), same
// "fixed by the platform" pill treatment as extractors.json's own `type`/
// `region`. Hard-cap helper text is always sourced from the fetched
// global_defaults (PRD 02 AC1, never hardcoded). Inline blur validation is a
// client-side UX nicety only - never the sole gate, so it never disables
// Save; serverErrors (from the PUT's own PRD 22 validation) take precedence
// when present.
export function LimitsSection({ limits, hardCaps, onChange, serverErrors }: LimitsSectionProps) {
  const [touched, setTouched] = useState<Partial<Record<LimitField, boolean>>>({})

  function handleChange(field: LimitField, value: string) {
    const numeric = Number(value)
    onChange({ ...limits, [field]: Number.isNaN(numeric) ? 0 : numeric })
  }

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Limits
        <InfoTooltip text="The most documents, pages, and file size a single request for this product can send. A request over any limit is rejected before processing starts." />
      </h2>
      <div className="grid grid-cols-4 gap-4">
        {FIELD_CONFIG.map(({ field, label, step }) => {
          const cap = hardCaps[field]
          const exceedsCap = touched[field] && limits[field] > cap
          const message =
            serverErrors?.[field] ?? (exceedsCap ? `Exceeds platform maximum of ${cap}` : undefined)

          return (
            <NumericField
              key={field}
              id={`limit-${field}`}
              label={label}
              step={step}
              value={limits[field]}
              onChange={(raw) => handleChange(field, raw)}
              onBlur={() => setTouched((current) => ({ ...current, [field]: true }))}
              error={message}
              helperText={<span className="text-meta text-text-secondary">Platform maximum: {cap}</span>}
            />
          )
        })}

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Document ingestion mode
            <InfoTooltip text="Fixed by the platform - inline_only (documents sent inline in the request body) is currently the only supported mode." />
          </span>
          <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
            {limits.document_ingestion_mode}
          </span>
        </div>
      </div>
    </section>
  )
}
